import React, { useState, useEffect } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {
  Camera,
  Tag,
  Box,
  Info,
  Plus,
  Grid,
  LayoutGrid,
  Package,
  Trash2,
  Send,
} from "lucide-react";
import { Footer } from "./Home";
import "../assets/css/Home.scss";
import { API_URL, IMAGE_PATH } from "../config/AppConfig";
import axios from "axios";
import Swal from "sweetalert2";

const AddProduct = () => {
  const token = sessionStorage.getItem("token");
  const user_id = sessionStorage.getItem("id");
  const shouldShowFooter = !token;
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editProductFromState = location.state?.editProduct;

  const [isEditMode, setIsEditMode] = useState(false);
  const [editProductId, setEditProductId] = useState(null);
  const [prefillLoading, setPrefillLoading] = useState(false);

  const [viewMode, setViewMode] = useState("card");
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [customCategory, setCustomCategory] = useState("");
  const [customBrand, setCustomBrand] = useState("");
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);

  const createEmptyProduct = () => ({
    id: Date.now(),
    product_name: "",
    category_id: "",
    brand_id: "",
    specification_description: "",
    product_description: "",
    price: "",
    show_price: false,
    minimum_order_quantity: "",
    available_quantity: "",
    images: [],
    imageFiles: [],
    existingImages: [],
  });

  const [products, setProducts] = useState([createEmptyProduct()]);

  const fetchCategory = () => {
    // setLoading(true);
    axios
      .get(`${API_URL}/get_category`)
      .then((response) => {
        if (response.data.success) {
          setCategories(response.data.categories_arr || []);
        }
      })
      .catch((error) => console.error("Error:", error))
    // .finally(() => setLoading(false));
  };

  const fetchBrand = (categoryId) => {
    axios.get(`${API_URL}/get_brand?category_id=${categoryId}&user_id=${user_id}`, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    })
      .then(res => {
        if (res.data.success) {
          setBrands(res.data.brand_arr);
        }
      })
      .catch(err => console.log("Brand Error:", err));
  };


  const addNewCategory = () => {
    axios.post(`${API_URL}/add_category`, {
      category_name: customCategory
    }, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(res => {
      if (res.data.success) {

        const newId = res.data.class_data.category_id;

        setSelectedCategoryId(newId);

        // update product value
        const updated = [...products];
        updated.forEach(p => {
          if (p.category_id === "other") {
            p.category_id = newId;
          }
        });
        setProducts(updated);

        fetchCategory();
        Swal.fire("Success", "Admin approval pending", "success");
      }
    });
  };

  const productIdFromParams = searchParams.get("productId");

  const mapProductToFormState = (productData) => {
    if (!productData) return null;

    const hasImageArray =
      Array.isArray(productData.images) && productData.images.length > 0;

    const imagesFromApi = hasImageArray
      ? productData.images
          .filter((img) => img?.image && img.image !== "NA")
          .map((img) =>
            img.image.startsWith("http")
              ? img.image
              : `${IMAGE_PATH}${img.image}`
          )
      : productData.image
      ? [
          productData.image.startsWith("http")
            ? productData.image
            : `${IMAGE_PATH}${productData.image}`,
        ]
      : [];

    return {
      id: productData.product_id || Date.now(),
      product_name: productData.product_name || "",
      category_id: productData.category_id
        ? productData.category_id.toString()
        : "",
      brand_id: productData.brand_id ? productData.brand_id.toString() : "",
      specification_description:
        productData.specification_description ||
        productData.specification ||
        "",
      product_description:
        productData.product_description || productData.description || "",
      price: productData.price || "",
      show_price:
        productData.price_status === 1 ||
        productData.show_price === 1 ||
        productData.show_price === true,
      minimum_order_quantity:
        productData.minimum_order_quantity ||
        productData.min_order_qty ||
        productData.quantity ||
        "",
      available_quantity:
        productData.available_quantity ||
        productData.available_qty ||
        productData.avg_quantity ||
        "",
      images: [],
      imageFiles: [],
      existingImages: imagesFromApi,
    };
  };

  const initializeEditMode = (productData) => {
    const mappedProduct = mapProductToFormState(productData);
    if (!mappedProduct) return;
    setIsEditMode(true);
    setEditProductId(productData.product_id || productData.id);
    setProducts([mappedProduct]);
    setViewMode("card");
    if (mappedProduct.category_id) {
      setSelectedCategoryId(mappedProduct.category_id);
      fetchBrand(mappedProduct.category_id);
    }
  };

  const resetEditMode = () => {
    setIsEditMode(false);
    setEditProductId(null);
    setProducts([createEmptyProduct()]);
  };

  const fetchProductDetails = async (productId) => {
    if (!productId) return;
    setPrefillLoading(true);
    try {
      const response = await axios.get(`${API_URL}/get_product_details`, {
        params: {
          user_id,
          product_id: productId,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data.success) {
        const productData =
          response.data.product ||
          (Array.isArray(response.data.product_arr)
            ? response.data.product_arr[0]
            : null);
        if (productData) {
          initializeEditMode(productData);
        }
      } else {
        Swal.fire({
          text: response.data.msg || "Unable to fetch product details",
          icon: "error",
        });
      }
    } catch (error) {
      console.error("Error fetching product details:", error);
      Swal.fire({
        text: "Unable to load product details. Please try again.",
        icon: "error",
      });
    } finally {
      setPrefillLoading(false);
    }
  };


  const addNewBrand = () => {
    axios.post(`${API_URL}/add_user_brand`, {
      brand_name: customBrand,
      category_id: selectedCategoryId
    }, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(res => {
      if (res.data.success) {

        const newBrandId = res.data.class_data.brand_id;


        const updated = [...products];
        updated.forEach(p => {
          if (p.brand_id === "other") {
            p.brand_id = newBrandId;
          }
        });
        setProducts(updated);

        fetchBrand(selectedCategoryId);
        Swal.fire("Success", "Admin approval pending", "success");
      }
    });
  };



  useEffect(() => {
    fetchCategory();
  }, []);

  useEffect(() => {
    if (editProductFromState) {
      initializeEditMode(editProductFromState);
    } else if (productIdFromParams) {
      fetchProductDetails(productIdFromParams);
    } else {
      resetEditMode();
    }
  }, [editProductFromState, productIdFromParams]);


  // ====================== HANDLE FORM VALUE CHANGE ======================== //
  const handleChange = (productIndex, field, value) => {
    const updatedProducts = [...products];
    updatedProducts[productIndex][field] = value;
    setProducts(updatedProducts);
  };

  // ====================== IMAGE UPLOAD =================================== //
  const handleImageUpload = (productIndex, files) => {
    const updatedProducts = [...products];
    const selectedProduct = { ...updatedProducts[productIndex] };
    const fileArray = Array.from(files);
    const newImages = fileArray.map((file) => URL.createObjectURL(file));

    selectedProduct.images = [...selectedProduct.images, ...newImages];
    selectedProduct.imageFiles = [
      ...(selectedProduct.imageFiles || []),
      ...fileArray,
    ];

    updatedProducts[productIndex] = selectedProduct;
    setProducts(updatedProducts);
  };

  // ====================== REMOVE PRODUCT CARD ============================ //
  const handleRemoveProduct = (productIndex) => {
    if (isEditMode) return;
    if (window.confirm("Are you sure you want to remove this product?")) {
      const updated = products.filter((_, i) => i !== productIndex);
      setProducts(updated);
    }
  };

  // ====================== ADD NEW PRODUCT CARD =========================== //
  const handleAddProduct = () => {
    if (isEditMode) return;
    setProducts([...products, createEmptyProduct()]);
  };

  // ====================== SUBMIT PRODUCT API ============================= //

  const handleCreateProducts = (e) => {
    e.preventDefault();

    const formData = new FormData();

    const user_id = sessionStorage.getItem("id");
    const approve_status = sessionStorage.getItem("approve_status");
    const subscription_id = sessionStorage.getItem("subscription_id");

    formData.append("user_id", user_id);
    formData.append("product_approve_status", approve_status);
    formData.append("user_subscription_id", subscription_id);

    const correctedProducts = products.map((p) => ({
      user_id: user_id,
      product_name: p.product_name,
      category_id: p.category_id,
      brand_id: p.brand_id,
      specification: p.specification_description,
      product_description: p.product_description,
      price: p.price,
      price_status: p.show_price ? 1 : 0,
      quantity: p.minimum_order_quantity,
      avg_quantity: p.available_quantity
    }));

    formData.append("products", JSON.stringify(correctedProducts));

    products.forEach((p, i) => {
      (p.imageFiles || []).forEach((file) => {
        formData.append(`images_product_${i}[]`, file);
      });
    });

    axios.post(`${API_URL}/add_product`, formData, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "multipart/form-data",
      },
    })
      .then((response) => {
        if (response.data.success) {

          Swal.fire({
            text: response.data.message || "Admin approval pending",
            icon: "success",
          });

          // 🔥 RESET PRODUCTS AFTER SUCCESS
          setProducts([createEmptyProduct()]);

        } else {
          Swal.fire({
            text: response.data.message,
            icon: "error",
          });
        }
      })
      .catch((error) => {
        Swal.fire({
          text: error?.response?.data?.message || "Something went wrong",
          icon: "error",
        });
      });
  };

  const handleUpdateProduct = (e) => {
    e.preventDefault();
    if (!isEditMode || !editProductId || products.length === 0) return;

    const product = products[0];
    const formData = new FormData();

    formData.append("product_id", editProductId);
    formData.append("user_id", user_id);
    formData.append("product_name", product.product_name);
    formData.append("category_id", product.category_id);
    formData.append("brand_id", product.brand_id);
    formData.append("specification", product.specification_description);
    formData.append("product_description", product.product_description);
    formData.append("price", product.price);
    formData.append("price_status", product.show_price ? 1 : 0);
    formData.append("min_order_qty", product.minimum_order_quantity);
    formData.append("available_qty", product.available_quantity);
    formData.append("quantity", product.minimum_order_quantity);
    formData.append("avg_quantity", product.available_quantity);

    (product.imageFiles || []).forEach((file, index) => {
      formData.append(`images_${index}`, file);
    });

    axios
      .post(`${API_URL}/edit_product`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      })
      .then((response) => {
        if (response.data.success) {
          Swal.fire({
            text: response.data.message || "Product updated successfully",
            icon: "success",
          });
          navigate("/my-products", { replace: true });
        } else {
          Swal.fire({
            text: response.data.msg || "Failed to update product",
            icon: "error",
          });
        }
      })
      .catch((error) => {
        Swal.fire({
          text:
            error?.response?.data?.message ||
            "Something went wrong while updating the product",
          icon: "error",
        });
      });
  };



  if (prefillLoading) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center" style={{ backgroundColor: "#f8f9fa" }}>
        <div className="text-center">
          <div className="spinner-border text-primary mb-3" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="text-muted mb-0">Loading product details...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-vh-100 d-flex flex-column" style={{ backgroundColor: "#f8f9fa" }}>
      <div className="flex-grow-1 d-flex flex-column">

        {/* ---------------------- TITLE CARD ---------------------- */}
        <div className="container my-4">
          <div
            className="card shadow-sm border-0 p-4 text-center"
            style={{
              borderRadius: "20px",
              background: "linear-gradient(135deg, #dbeafe, #bfdbfe)",
            }}
          >
            <div className="d-flex justify-content-center mb-3">
              <div
                className="d-flex justify-content-center align-items-center rounded-circle"
                style={{
                  width: "80px",
                  height: "80px",
                  background: "linear-gradient(135deg, #2563eb, #38bdf8)",
                }}
              >
                <Package size={32} color="#ffffff" />
              </div>
            </div>

            <h5 className="fw-bold mb-2">
              {isEditMode ? "Edit Product" : "Add New Product"}
            </h5>
            <p className="text-muted mb-0">
              {isEditMode
                ? "Update the details below to modify your product"
                : "Fill in the details to add your product"}
            </p>
          </div>
        </div>

        {/* ---------------------- VIEW TOGGLE (CARD <-> TABLE) ---------------------- */}
        <div className="container mb-3">
          <div className="d-flex gap-2 p-2 bg-white rounded-pill shadow-sm">
            <button
              onClick={() => setViewMode("card")}
              className={`flex-fill btn rounded-pill ${viewMode === "card" ? "text-white" : "text-muted"
                }`}
              style={{
                background:
                  viewMode === "card"
                    ? "linear-gradient(90deg, #1d4ed8, #2563eb)"
                    : "transparent",
                border: "none",
              }}
            >
              <Grid size={16} className="me-2" />
              Card view
            </button>

            <button
              onClick={() => setViewMode("table")}
              className={`flex-fill btn rounded-pill ${viewMode === "table" ? "text-white" : "text-muted"
                }`}
              style={{
                background:
                  viewMode === "table"
                    ? "linear-gradient(90deg, #1d4ed8, #2563eb)"
                    : "transparent",
                border: "none",
              }}
            >
              <LayoutGrid size={16} className="me-2" />
              Table view
            </button>
          </div>
        </div>

        {/* ---------------------- MAIN FORM SECTION ---------------------- */}
        <div className="container mb-4 flex-grow-1">
          {viewMode === "card" ? (
            // ============================================================
            // ========================== CARD VIEW =======================
            // ============================================================
            <div className="row g-3">
              {products.map((product, index) => (
                <div key={product.id} className="col-12">
                  <div className="card shadow-sm border-0 p-4" style={{ borderRadius: "20px" }}>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h6 className="fw-bold" style={{ color: "#1d4ed8" }}>
                        Product Details
                      </h6>

                      {!isEditMode && (
                        <button
                          onClick={() => handleRemoveProduct(index)}
                          className="btn btn-sm btn-outline-danger d-flex align-items-center gap-1"
                        >
                          <Trash2 size={16} /> Remove
                        </button>
                      )}
                    </div>

                    {/* ---------------- CATEGORY (DYNAMIC) ---------------- */}
                    {/* <div className="mb-3">
                      <label className="form-label fw-semibold">
                        Category <span className="text-danger">*</span>
                      </label>

                      <div
                        className="input-group"
                        style={{
                          backgroundColor: "#eef2ff",
                          borderRadius: "12px",
                          padding: "4px",
                        }}
                      >
                        <span className="input-group-text bg-transparent border-0">
                          <Box size={20} color="#1d4ed8" />
                        </span>

                        <select
                          className="form-select border-0 bg-transparent"
                          value={product.category}
                          onChange={(e) =>
                            handleChange(index, "category", e.target.value)
                          }
                        >
                          <option value="">Select Category</option>

                          {categories.map((cat, i) => (
                            <option key={i} value={cat.category_name}>
                              {cat.category_name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div> */}

                    <div className="mb-3">
                      <label className="form-label fw-semibold">
                        Category <span className="text-danger">*</span>
                      </label>

                      <div className="input-group"
                        style={{
                          backgroundColor: "#eef2ff",
                          borderRadius: "12px",
                          padding: "4px"
                        }}
                      >
                        <span className="input-group-text bg-transparent border-0">
                          <Box size={20} color="#1d4ed8" />
                        </span>

                        <select
                          className="form-select border-0 bg-transparent"
                          value={product.category_id}
                          onChange={(e) => {
                            const value = e.target.value;

                            handleChange(index, "category_id", value);

                            if (value === "other") {
                              setCustomCategory("");
                              return;
                            }

                            // update selected and fetch brand
                            setSelectedCategoryId(value);
                            fetchBrand(value);
                          }}
                        >
                          <option value="">Select Category</option>

                          {categories.map(cat => (
                            <option key={cat.category_id} value={cat.category_id}>
                              {cat.category_name}
                            </option>
                          ))}

                          <option value="other">Other</option>
                        </select>

                      </div>

                      {/* Show Input Field if Other Selected */}
                      {product.category_id === "other" && (
                        <input
                          type="text"
                          className="form-control mt-2"
                          placeholder="Enter Custom Category"
                          value={customCategory}
                          onChange={(e) => setCustomCategory(e.target.value)}
                          onBlur={() => {
                            if (customCategory.trim() !== "") {
                              addNewCategory();
                            }
                          }}
                        />
                      )}

                    </div>


                    {/* ---------------- PRODUCT IMAGES ---------------- */}
                    <div className="mb-3">
                      <div className="d-flex justify-content-between mb-2">
                        <label className="form-label fw-semibold mb-0">
                          Product Images <span className="text-danger">*</span>
                        </label>
                        <span className="badge bg-success">Unlimited</span>
                      </div>

                      <div className="d-flex gap-2 flex-wrap">
                        {product.existingImages?.map((img, i) => (
                          <img
                            key={`existing-${product.id}-${i}`}
                            src={img}
                            alt="existing"
                            style={{
                              width: "80px",
                              height: "80px",
                              objectFit: "cover",
                              borderRadius: "12px",
                            }}
                          />
                        ))}
                        {product.images.map((img, i) => (
                          <img
                            key={`new-${product.id}-${i}`}
                            src={img}
                            alt="preview"
                            style={{
                              width: "80px",
                              height: "80px",
                              objectFit: "cover",
                              borderRadius: "12px",
                            }}
                          />
                        ))}

                        <label
                          htmlFor={`imageUpload-${index}`}
                          style={{
                            width: "80px",
                            height: "80px",
                            borderRadius: "12px",
                            cursor: "pointer",
                            background: "linear-gradient(135deg, #dbeafe, #bfdbfe)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <Camera size={24} color="#1d4ed8" />
                        </label>

                        <input
                          type="file"
                          id={`imageUpload-${index}`}
                          accept="image/*"
                          multiple
                          className="d-none"
                          onChange={(e) => handleImageUpload(index, e.target.files)}
                        />
                      </div>
                    </div>

                    {/* ---------------- PRODUCT NAME ---------------- */}
                    <div className="mb-3">
                      <label className="form-label fw-semibold">Product Name *</label>

                      <div
                        className="input-group"
                        style={{
                          backgroundColor: "#eef2ff",
                          borderRadius: "12px",
                          padding: "4px",
                        }}
                      >
                        <span className="input-group-text bg-transparent border-0">
                          <Tag size={20} color="#1d4ed8" />
                        </span>

                        <input
                          type="text"
                          className="form-control border-0 bg-transparent"
                          placeholder="Product Name"
                          value={product.product_name}
                          onChange={(e) =>
                            handleChange(index, "product_name", e.target.value)
                          }
                        />
                      </div>
                    </div>

                    {/* ---------------- BRAND ---------------- */}
                    {/* <div className="mb-3">
                      <label className="form-label fw-semibold">Brand *</label>

                      <div
                        className="input-group"
                        style={{
                          backgroundColor: "#eef2ff",
                          borderRadius: "12px",
                          padding: "4px",
                        }}
                      >
                        <span className="input-group-text bg-transparent border-0">
                          <Package size={20} color="#1d4ed8" />
                        </span>

                        <select
                          className="form-select border-0 bg-transparent"
                          value={product.brand}
                          onChange={(e) =>
                            handleChange(index, "brand", e.target.value)
                          }
                        >
                          <option value="">Select Brand</option>
                          <option value="Brand 1">Brand 1</option>
                          <option value="Brand 2">Brand 2</option>
                        </select>
                      </div>
                    </div> */}

                    <div className="mb-3">
                      <label className="form-label fw-semibold">Brand *</label>

                      <div className="input-group"
                        style={{
                          backgroundColor: "#eef2ff",
                          borderRadius: "12px",
                          padding: "4px"
                        }}
                      >
                        <span className="input-group-text bg-transparent border-0">
                          <Package size={20} color="#1d4ed8" />
                        </span>

                        <select
                          className="form-select border-0 bg-transparent"
                          value={product.brand_id}
                          onChange={(e) => {
                            const value = e.target.value;

                            handleChange(index, "brand_id", value);

                            if (value === "other") {
                              setCustomBrand("");
                            }
                          }}
                        >
                          <option value="">Select Brand</option>

                          {brands.map(br => (
                            <option key={br.brand_id} value={br.brand_id}>
                              {br.brand_name}
                            </option>
                          ))}

                          <option value="other">Other</option>
                        </select>

                      </div>

                      {/* Custom Brand Input */}
                      {product.brand_id === "other" && (
                        <input
                          type="text"
                          className="form-control mt-2"
                          placeholder="Enter Custom Brand"
                          value={customBrand}
                          onChange={(e) => setCustomBrand(e.target.value)}
                          onBlur={() => {
                            if (customBrand.trim()) addNewBrand();
                          }}
                        />
                      )}

                    </div>


                    {/* ---------------- SPECIFICATION DESCRIPTION ---------------- */}
                    <div className="mb-3">
                      <label className="form-label fw-semibold">
                        Specification Description *
                      </label>

                      <textarea
                        className="form-control"
                        rows="3"
                        placeholder="Specification description"
                        value={product.specification_description}
                        onChange={(e) =>
                          handleChange(index, "specification_description", e.target.value)
                        }
                        style={{
                          backgroundColor: "#eef2ff",
                          borderRadius: "12px",
                        }}
                      />
                    </div>

                    {/* ---------------- PRODUCT DESCRIPTION ---------------- */}
                    <div className="mb-3">
                      <label className="form-label fw-semibold">
                        Product Description *
                      </label>

                      <textarea
                        className="form-control"
                        rows="3"
                        placeholder="Product description"
                        value={product.product_description}
                        onChange={(e) =>
                          handleChange(index, "product_description", e.target.value)
                        }
                        style={{
                          backgroundColor: "#eef2ff",
                          borderRadius: "12px",
                        }}
                      />
                    </div>

                    {/* ---------------- PRICE ---------------- */}
                    <div className="mb-3">
                      <label className="form-label fw-semibold">
                        Price per product *
                      </label>

                      <div
                        className="input-group"
                        style={{
                          backgroundColor: "#eef2ff",
                          borderRadius: "12px",
                          padding: "4px",
                        }}
                      >
                        <span
                          className="input-group-text bg-transparent border-0 fw-bold"
                          style={{ color: "#1d4ed8" }}
                        >
                          AED
                        </span>

                        <input
                          type="number"
                          className="form-control border-0 bg-transparent"
                          placeholder="Price per product"
                          value={product.price}
                          onChange={(e) => handleChange(index, "price", e.target.value)}
                        />
                      </div>
                    </div>

                    {/* ---------------- SHOW PRICE CHECKBOX ---------------- */}
                    <div
                      className="mb-3 p-3"
                      style={{
                        backgroundColor: "#eef2ff",
                        borderRadius: "12px",
                      }}
                    >
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={product.show_price}
                          onChange={(e) =>
                            handleChange(index, "show_price", e.target.checked)
                          }
                        />
                        <label className="form-check-label">
                          Show price on product details
                        </label>
                      </div>
                    </div>

                    {/* ---------------- MIN ORDER QTY ---------------- */}
                    <div className="mb-3">
                      <label className="form-label fw-semibold">Minimum Order Quantity *</label>

                      <div
                        className="input-group"
                        style={{
                          backgroundColor: "#eef2ff",
                          borderRadius: "12px",
                          padding: "4px",
                        }}
                      >
                        <span className="input-group-text bg-transparent border-0">
                          <Info size={20} color="#1d4ed8" />
                        </span>

                        <input
                          type="number"
                          className="form-control border-0 bg-transparent"
                          placeholder="Minimum Order Quantity"
                          value={product.minimum_order_quantity}
                          onChange={(e) =>
                            handleChange(index, "minimum_order_quantity", e.target.value)
                          }
                        />
                      </div>
                    </div>

                    {/* ---------------- AVAILABLE QUANTITY ---------------- */}
                    <div className="mb-3">
                      <label className="form-label fw-semibold">Available Quantity *</label>

                      <div
                        className="input-group"
                        style={{
                          backgroundColor: "#eef2ff",
                          borderRadius: "12px",
                          padding: "4px",
                        }}
                      >
                        <span className="input-group-text bg-transparent border-0">
                          <Box size={20} color="#1d4ed8" />
                        </span>

                        <input
                          type="number"
                          className="form-control border-0 bg-transparent"
                          placeholder="Available Quantity"
                          value={product.available_quantity}
                          onChange={(e) =>
                            handleChange(index, "available_quantity", e.target.value)
                          }
                          disabled={isEditMode}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              ))}

              {/* ---------------- ADD NEW PRODUCT BUTTON ---------------- */}
              {!isEditMode && (
                <div
                  onClick={handleAddProduct}
                  className="card shadow-sm border-0 p-3 text-center"
                  style={{
                    borderRadius: "16px",
                    background: "linear-gradient(135deg, #dbeafe, #bfdbfe)",
                    cursor: "pointer",
                  }}
                >
                  <div className="d-flex align-items-center justify-content-center gap-2">
                    <Plus size={24} color="#1d4ed8" />
                    <h6 className="mb-0 fw-bold" style={{ color: "#1d4ed8" }}>
                      Add Product
                    </h6>
                  </div>
                </div>
              )}
            </div>
          ) : (
            // ============================================================
            // ========================== TABLE VIEW ======================
            // ============================================================
            <div className="container-fluid p-4">
              <div
                className="card shadow-sm border-0"
                style={{ borderRadius: "20px", overflow: "hidden" }}
              >
                <div className="table-responsive" style={{
                  maxHeight: '600px',
                  overflowY: 'auto',
                  overflowX: 'auto'
                }}>
                  <table className="table table-hover align-middle mb-0">
                    <thead style={{
                      background: "#eef2ff",
                      position: 'sticky',
                      top: 0,
                      zIndex: 10
                    }}>

                      <tr>
                        <th style={{ minWidth: '150px' }}>Images</th>
                        <th style={{ minWidth: '200px' }}>Product Name</th>
                        <th style={{ minWidth: '250px' }}>Category</th>
                        <th style={{ minWidth: '250px' }}>Brand</th>
                        <th style={{ minWidth: '250px' }}>Specification</th>
                        <th style={{ minWidth: '250px' }}>Description</th>
                        <th style={{ minWidth: '150px' }}>Price</th>
                        <th style={{ minWidth: '80px' }}>Show</th>
                        <th style={{ minWidth: '120px' }}>Min Qty</th>
                        <th style={{ minWidth: '120px' }}>Avail Qty</th>
                        <th style={{ minWidth: '100px' }}>Action</th>
                      </tr>
                    </thead>

                    <tbody>
                      {products.map((product, index) => (
                        <tr key={product.id}>
                          <td>
                            <div className="d-flex gap-1 align-items-center">
                              {[...(product.existingImages || []), ...product.images]
                                .slice(0, 2)
                                .map((img, i) => (
                                  <img
                                    key={`${product.id}-table-${i}`}
                                    src={img}
                                    alt="img"
                                    style={{
                                      width: "40px",
                                      height: "40px",
                                      borderRadius: "8px",
                                      objectFit: "cover",
                                    }}
                                  />
                                ))}

                              <label
                                htmlFor={`imageUpload-${index}`}
                                className="btn btn-sm btn-outline-primary"
                              >
                                <Camera size={14} />
                              </label>

                              <input
                                type="file"
                                id={`imageUpload-${index}`}
                                multiple
                                accept="image/*"
                                className="d-none"
                                onChange={(e) =>
                                  handleImageUpload(index, e.target.files)
                                }
                              />
                            </div>
                          </td>

                          <td>
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              value={product.product_name}
                              onChange={(e) =>
                                handleChange(index, "product_name", e.target.value)
                              }
                            />
                          </td>

                          <td>
                            <div>
                              <select
                                className="form-select form-select-sm"
                                value={product.category_id}
                                onChange={(e) => {
                                  const value = e.target.value;

                                  handleChange(index, "category_id", value);

                                  if (value === "other") {
                                    setCustomCategory("");
                                    return;
                                  }

                                  setSelectedCategoryId(value);
                                  fetchBrand(value);
                                }}
                              >
                                <option value="">Select</option>

                                {categories.map((cat) => (
                                  <option key={cat.category_id} value={cat.category_id}>
                                    {cat.category_name}
                                  </option>
                                ))}

                                <option value="other">Other</option>
                              </select>

                              {/* Custom Category Input */}
                              {product.category_id === "other" && (
                                <input
                                  type="text"
                                  className="form-control form-control-sm mt-1"
                                  placeholder="Enter Custom Category"
                                  value={customCategory}
                                  onChange={(e) => setCustomCategory(e.target.value)}
                                  onBlur={() => {
                                    if (customCategory.trim()) addNewCategory();
                                  }}
                                />
                              )}
                            </div>
                          </td>


                          <td>
                            <div>
                              <select
                                className="form-select form-select-sm"
                                value={product.brand_id}
                                onChange={(e) => {
                                  const value = e.target.value;

                                  handleChange(index, "brand_id", value);

                                  if (value === "other") {
                                    setCustomBrand("");
                                  }
                                }}
                              >
                                <option value="">Brand</option>

                                {brands.map((br) => (
                                  <option key={br.brand_id} value={br.brand_id}>
                                    {br.brand_name}
                                  </option>
                                ))}

                                <option value="other">Other</option>
                              </select>

                              {/* Custom Brand Input */}
                              {product.brand_id === "other" && (
                                <input
                                  type="text"
                                  className="form-control form-control-sm mt-1"
                                  placeholder="Enter Custom Brand"
                                  value={customBrand}
                                  onChange={(e) => setCustomBrand(e.target.value)}
                                  onBlur={() => {
                                    if (customBrand.trim()) addNewBrand();
                                  }}
                                />
                              )}
                            </div>
                          </td>


                          <td>
                            <textarea
                              className="form-control form-control-sm"
                              rows="2"
                              value={product.specification_description}
                              onChange={(e) =>
                                handleChange(
                                  index,
                                  "specification_description",
                                  e.target.value
                                )
                              }
                            />
                          </td>

                          <td>
                            <textarea
                              className="form-control form-control-sm"
                              rows="2"
                              value={product.product_description}
                              onChange={(e) =>
                                handleChange(
                                  index,
                                  "product_description",
                                  e.target.value
                                )
                              }
                            />
                          </td>

                          <td>
                            <input
                              type="number"
                              className="form-control form-control-sm"
                              value={product.price}
                              onChange={(e) =>
                                handleChange(index, "price", e.target.value)
                              }
                            />
                          </td>

                          <td className="text-center">
                            <input
                              type="checkbox"
                              checked={product.show_price}
                              onChange={(e) =>
                                handleChange(index, "show_price", e.target.checked)
                              }
                            />
                          </td>

                          <td>
                            <input
                              type="number"
                              className="form-control form-control-sm"
                              value={product.minimum_order_quantity}
                              onChange={(e) =>
                                handleChange(
                                  index,
                                  "minimum_order_quantity",
                                  e.target.value
                                )
                              }
                            />
                          </td>

                          <td>
                            <input
                              type="number"
                              className="form-control form-control-sm"
                              value={product.available_quantity}
                              onChange={(e) =>
                                handleChange(
                                  index,
                                  "available_quantity",
                                  e.target.value
                                )
                              }
                            disabled={isEditMode}
                            />
                          </td>

                          <td>
                            <button
                              className="btn btn-danger btn-sm"
                              onClick={() => handleRemoveProduct(index)}
                            >
                              <Trash2 size={14} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              {!isEditMode && (
                <div
                  onClick={handleAddProduct}
                  className="card shadow-sm border-0 p-3 text-center mt-5"
                  style={{
                    borderRadius: "16px",
                    background: "linear-gradient(135deg, #dbeafe, #bfdbfe)",
                    cursor: "pointer",
                  }}
                >
                  <div className="d-flex align-items-center justify-content-center gap-2">
                    <Plus size={24} color="#1d4ed8" />
                    <h6 className="mb-0 fw-bold" style={{ color: "#1d4ed8" }}>
                      Add Product
                    </h6>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* ---------------------- SUBMIT BUTTON ---------------------- */}

        <div className="container text-end mb-5">
          <button
            onClick={isEditMode ? handleUpdateProduct : handleCreateProducts}
            className="btn flex-fill w-100 fw-semibold text-white"
            style={{
              borderRadius: "16px",
              background: "linear-gradient(90deg, #f44336, #e91e63)",
              padding: "14px",
            }}
          >
            <Send size={20} className="me-2" />
            {isEditMode ? "Update Product" : "Post/Publish"}
          </button>
        </div>
      </div>

      {shouldShowFooter && <Footer />}
    </div>
  );
};

export default AddProduct;
